import React, { useState } from "react";
import { updateDoc, doc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth, storage } from "../../firebaseConfig";
import { useUserData } from "../../hooks/useUsers";
import jsonData from "../../data/data.json";
import dayjs from "dayjs";
import maleLogo from "../../assets/images/icons8-man-in-tuxedo-48.png";
import femaleLogo from "../../assets/images/icons8-female-48.png";
import kidsLogo from "../../assets/images/icons8-kids-40.png";
import noKidsLogo from "../../assets/images/icons8-no-kids-50.png";
import { useNavigate } from "react-router-dom";
import {
  OrderedListOutlined,
  PlusOutlined,
  DeleteFilled,
  UserOutlined,
  BookOutlined,
  TeamOutlined,
  HeartOutlined,
  ContactsOutlined,
  CameraOutlined,
  EditOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import {
  Flex,
  Drawer,
  Menu,
  Form,
  Card,
  Row,
  Col,
  Typography,
  Input,
  InputNumber,
  Radio,
  Select,
  Button,
  Space,
  Switch,
  message,
  DatePicker,
  Upload,
  Progress,
  Spin,
  Badge,
  Alert,
  Tooltip
} from "antd";
import { v4 as uuidv4 } from "uuid";

// Compress image function
export async function compressImage(
  file,
  maxSize = 500 * 1024,
  quality = 0.7
) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      if (!result || typeof result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }

      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Resize if too large
        const maxDimension = 1920;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64
        const compressed = canvas.toDataURL('image/jpeg', quality);
        
        if (compressed.length > maxSize) {
          reject(new Error(`Image too large: ${Math.round(compressed.length / 1024)}KB. Max: ${Math.round(maxSize / 1024)}KB`));
          return;
        }

        resolve(compressed);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = result;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Convert base64 to File object for Firebase upload
const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while(n--){
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, {type:mime});
};

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

// Face recognition helper function
const detectFace = (file) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      // Simple face detection using aspect ratio and file size heuristics
      // In production, you'd use a proper face detection library like face-api.js
      const aspectRatio = img.width / img.height;
      const isPortrait = aspectRatio > 0.5 && aspectRatio < 2;
      const hasReasonableSize = file.size > 10000; // Basic size check
      
      if (isPortrait && hasReasonableSize) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function EditProfile() {
  let navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form] = Form.useForm();
  const [selectedMenuItem, setSelectedMenuItem] = useState("basic");
  const [user] = useAuthState(auth);
  const { loading: userDataLoading, userData } = useUserData(user?.uid);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (userDataLoading) return;
    if (userData && !userData.profileCompleted) {
      navigate("/onboarding");
    } else {
      return;
    }
  }, [user, userData, userDataLoading, navigate]);

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const menuItems = [
    { key: "basic", label: "Basic Information", icon: <UserOutlined />, color: "#1890ff" },
    { key: "religious", label: "Religious Information", icon: <BookOutlined />, color: "#722ed1" },
    { key: "education", label: "Education Information", icon: <BookOutlined />, color: "#13c2c2" },
    { key: "family", label: "Family Information", icon: <TeamOutlined />, color: "#52c41a" },
    { key: "general", label: "General Information", icon: <InfoCircleOutlined />, color: "#fa8c16" },
    { key: "contact", label: "Contact Information", icon: <ContactsOutlined />, color: "#eb2f96" },
    { key: "photos", label: "Photos Information", icon: <CameraOutlined />, color: "#f5222d" },
    { key: "aboutSelf", label: "About Self Information", icon: <EditOutlined />, color: "#faad14" },
    { key: "aboutPartner", label: "About Partner Information", icon: <HeartOutlined />, color: "#fa541c" }
  ];

  const initialValues = [
    {
      name: ["profileId"],
      value: userData?.profileId,
    },
    {
      name: ["firstName"],
      value: userData?.firstName,
    },
    {
      name: ["lastName"],
      value: userData?.lastName,
    },
    {
      name: ["gender"],
      value: userData?.gender,
    },
    {
      name: ["maritalStatus"],
      value: userData?.maritalStatus,
    },
    {
      name: ["haveKids"],
      value: userData?.haveKids,
    },
    {
      name: ["kids"],
      value: userData?.kids || 0,
    },
    {
      name: ["kidsLivingStatus"],
      value: userData?.kidsLivingStatus || "",
    },
    {
      name: ["currentResidence"],
      value: userData?.currentResidence,
    },
    {
      name: ["citizenOf"],
      value: userData?.citizenOf,
    },
    {
      name: ["motherTongue"],
      value: userData?.motherTongue,
    },
    {
      name: ["spokenLanguages"],
      value: userData?.spokenLanguages,
    },
    {
      name: ["currentResidence"],
      value: userData?.currentResidence,
    },
    {
      name: ["subCaste"],
      value: userData?.subCaste,
    },
    {
      name: ["gothram"],
      value: userData?.gothram,
    },
    {
      name: ["nakshatram"],
      value: userData?.nakshatram,
    },
    {
      name: ["padam"],
      value: userData?.padam,
    },
    {
      name: ["raasi"],
      value: userData?.raasi || undefined,
    },
    {
      name: ["placeOfBirth"],
      value: userData?.placeOfBirth,
    },
    {
      name: ["dateOfBirth"],
      value: userData?.dateOfBirth ? dayjs(
        userData.dateOfBirth.toDate ?
          userData.dateOfBirth.toDate() :
          userData.dateOfBirth
      ) : null,
    },
    {
      name: ["timeOfBirth"],
      value: userData?.timeOfBirth,
    },
    {
      name: ["dosham"],
      value: userData?.dosham,
    },
    {
      name: ["doshamType"],
      value: userData?.doshamType,
    },
    {
      name: ["education"],
      value: userData?.education,
    },
    {
      name: ["educationDetails"],
      value: userData?.educationDetails,
    },
    {
      name: ["additionalEducationInfo"],
      value: userData?.additionalEducationInfo,
    },
    {
      name: ["employment"],
      value: userData?.employment,
    },
    {
      name: ["occupation"],
      value: userData?.occupation,
    },
    {
      name: ["additionalEmploymentInfo"],
      value: userData?.additionalEmploymentInfo,
    },
    {
      name: ["currency"],
      value: userData?.currency,
    },
    {
      name: ["salary"],
      value: userData?.salary,
    },
    {
      name: ["salaryType"],
      value: userData?.salaryType,
    },
    {
      name: ["fathersOccupation"],
      value: userData?.fathersOccupation,
    },
    {
      name: ["fathersName"],
      value: userData?.fathersName,
    },
    {
      name: ["mothersOccupation"],
      value: userData?.mothersOccupation,
    },
    {
      name: ["mothersName"],
      value: userData?.mothersName,
    },
    {
      name: ["brothers"],
      value: userData?.brothers,
    },
    {
      name: ["brothersMarital"],
      value: userData?.brothersMarital,
    },
    {
      name: ["sisters"],
      value: userData?.sisters,
    },
    {
      name: ["sistersMarital"],
      value: userData?.sistersMarital,
    },
    {
      name: ["familyValues"],
      value: userData?.familyValues,
    },
    {
      name: ["familyStatus"],
      value: userData?.familyStatus,
    },
    {
      name: ["familyType"],
      value: userData?.familyType,
    },
    {
      name: ["nativePlace"],
      value: userData?.nativePlace,
    },
    {
      name: ["smoking"],
      value: userData?.smoking,
    },
    {
      name: ["tobacco"],
      value: userData?.tobacco,
    },
    {
      name: ["drinking"],
      value: userData?.drinking,
    },
    {
      name: ["drugs"],
      value: userData?.drugs,
    },
    {
      name: ["diet"],
      value: userData?.diet,
    },
    {
      name: ["height"],
      value: userData?.height,
    },
    {
      name: ["weight"],
      value: userData?.weight,
    },
    {
      name: ["weightType"],
      value: userData?.weightType,
    },
    {
      name: ["bodyType"],
      value: userData?.bodyType,
    },
    {
      name: ["disability"],
      value: userData?.disability,
    },
    {
      name: ["contact"],
      value: userData?.contact,
    },
    {
      name: ["profileImages"],
      value: userData?.profileImages,
    },
    {
      name: ["aboutSelf"],
      value: userData?.aboutSelf,
    },
    {
      name: ["partnerPreferences"],
      value: userData?.partnerPreferences,
    },
  ];

  // UPDATED: Storage image function with compression
  const storageImage = async (img) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Get original file info
        const originalFile = img.originFileObj;
        const originalSizeKB = Math.round(originalFile.size / 1024);
        
        // Show compression loading
        message.loading({ content: 'Compressing image...', key: 'compress' });
        
        // Compress the image first
        const compressedBase64 = await compressImage(originalFile, 500 * 1024, 0.7);
        
        // Convert back to File for upload
        const compressedFile = dataURLtoFile(compressedBase64, originalFile.name);
        const compressedSizeKB = Math.round(compressedFile.size / 1024);
        const compressionRatio = Math.round(((originalSizeKB - compressedSizeKB) / originalSizeKB) * 100);
        
        message.success({ 
          content: `Image compressed ${compressionRatio}% (${originalSizeKB}KB → ${compressedSizeKB}KB)`, 
          key: 'compress',
          duration: 3
        });

        // Upload compressed file to Firebase
        const storageRef = ref(storage, `/images/${Date.now()}-${uuidv4()}`);
        const uploadImage = uploadBytesResumable(storageRef, compressedFile);

        uploadImage.on(
          "state_changed",
          (snapshot) => {
            const progressPercent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setProgress(progressPercent);
          },
          (error) => {
            message.error('Upload failed. Please try again.');
            reject(error);
          },
          () => {
            getDownloadURL(uploadImage.snapshot.ref).then((downloadURL) => {
              resolve({ 
                uid: uuidv4(), 
                url: downloadURL,
                compressed: true,
                originalSizeKB,
                compressedSizeKB,
                compressionRatio
              });
            });
          }
        );
      } catch (compressionError) {
        message.error({ content: 'Image compression failed. Please try again.', key: 'compress' });
        reject(compressionError);
      }
    });
  };

  // UPDATED: Custom upload validation with compression support
  const beforeUpload = async (file, fileList) => {
    // Check file count limit (including existing files)
    const existingFiles = form.getFieldValue('profileImages') || [];
    const existingCount = existingFiles.filter(img => img.url).length;
    const newFilesCount = fileList.length;
    
    if (existingCount + newFilesCount > 6) {
      message.error('You can upload maximum 6 photos');
      return Upload.LIST_IGNORE;
    }

    // Check file size (max 10MB before compression)
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Image must be smaller than 10MB');
      return Upload.LIST_IGNORE;
    }

    // Check file type
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/webp';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG/WEBP files');
      return Upload.LIST_IGNORE;
    }

    // Face detection
    try {
      const hasFace = await detectFace(file);
      if (!hasFace) {
        message.error('Please upload a clear photo with your face visible');
        return Upload.LIST_IGNORE;
      }
    } catch (error) {
      console.error('Face detection error:', error);
      message.warning('Could not verify face in image, but upload will continue');
    }

    return true;
  };

  const handleFinish = async (values) => {
    setLoading(true);

    try {
      var profileImages = [];

      // Handle photo uploads if on photos tab
      if (selectedMenuItem === "photos") {
        try {
          // Calculate compression stats
          let totalOriginalSize = 0;
          let totalCompressedSize = 0;
          let newImagesCount = 0;

          profileImages = await Promise.all(
            [...values.profileImages].map(async (image) => {
              if (image.url) {
                // Existing image
                return image;
              } else {
                // New image - compress and upload
                newImagesCount++;
                const originalSizeKB = Math.round(image.originFileObj.size / 1024);
                totalOriginalSize += originalSizeKB;
                
                const result = await storageImage(image);
                totalCompressedSize += result.compressedSizeKB;
                
                return result;
              }
            })
          );

          if (newImagesCount > 0) {
            const totalSavings = Math.round(((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100);
            message.success(`${newImagesCount} new photo(s) uploaded with ${totalSavings}% compression savings!`);
          }
        } catch (error) {
          console.error("Image upload error:", error);
          message.error("Failed to upload images. Please try again.");
          setLoading(false);
          return;
        }
      }

      // Build profile document based on selected menu item
      const profileDoc =
        selectedMenuItem === "basic"
          ? {
            profileId: values.profileId,
            firstName: values.firstName,
            lastName: values.lastName,
            gender: values.gender,
            maritalStatus: values.maritalStatus,
            haveKids: values.haveKids,
            kids: values.kids || 0,
            kidsLivingStatus: values.kidsLivingStatus || "",
            currentResidence: values.currentResidence,
            citizenOf: values.citizenOf,
            motherTongue: values.motherTongue,
            spokenLanguages: values.spokenLanguages,
          }
          : selectedMenuItem === "religious"
            ? {
              subCaste: values.subCaste,
              gothram: values.gothram,
              nakshatram: values.nakshatram,
              padam: values.padam,
              raasi: values.raasi,
              dosham: values.dosham,
              doshamType: values.doshamType,
            }
            : selectedMenuItem === "education"
              ? {
                education: values.education,
                educationDetails: values.educationDetails,
                additionalEducationInfo: values.additionalEducationInfo,
                employment: values.employment,
                occupation: values.occupation,
                additionalEmploymentInfo: values.additionalEmploymentInfo,
                currency: values.currency,
                salary: values.salary,
                salaryType: values.salaryType,
              }
              : selectedMenuItem === "family"
                ? {
                  familyValues: values.familyValues,
                  familyStatus: values.familyStatus,
                  familyType: values.familyType,
                  fathersOccupation: values.fathersOccupation,
                  fathersName: values.fathersName,
                  mothersOccupation: values.mothersOccupation,
                  mothersName: values.mothersName,
                  brothers: values.brothers,
                  brothersMarital: values.brothersMarital,
                  sisters: values.sisters,
                  sistersMarital: values.sistersMarital,
                  nativePlace: values.nativePlace,
                }
                : selectedMenuItem === "general"
                  ? {
                    smoking: values.smoking,
                    tobacco: values.tobacco,
                    drinking: values.drinking,
                    drugs: values.drugs,
                    diet: values.diet,
                    height: values.height,
                    weight: values.weight,
                    weightType: values.weightType,
                    bodyType: values.bodyType,
                    disability: values.disability,
                  }
                  : selectedMenuItem === "contact"
                    ? {
                      contact: values.contact,
                    }
                    : selectedMenuItem === "photos"
                      ? {
                        profileImages: profileImages,
                        photosCompressed: true // Add compression flag
                      }
                      : selectedMenuItem === "aboutSelf"
                        ? {
                          aboutSelf: values.aboutSelf,
                        }
                        : selectedMenuItem === "aboutPartner"
                          ? {
                            partnerPreferences: values.partnerPreferences, // Fixed: was 'requirements'
                          }
                          : null;

      if (!profileDoc) {
        throw new Error("Invalid menu item selected");
      }

      // Update Firestore document
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, profileDoc);

      // Success
      message.success(`Information updated successfully.`, 2);
      setProgress(0);

    } catch (error) {
      console.error("Update error:", error);
      message.error(`Profile could not be updated. Please try again.`, 2);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const getCurrentMenuConfig = () => {
    return menuItems.find(item => item.key === selectedMenuItem);
  };

  const renderMenuContent = () => (
    <Menu
      selectedKeys={[selectedMenuItem]}
      mode="vertical"
      onClick={(e) => {
        setSelectedMenuItem(e.key);
        setVisible(false); // Close drawer on mobile after selection
      }}
      style={{ border: 'none' }}
    >
      {menuItems.map((item) => (
        <Menu.Item 
          key={item.key}
          style={{
            borderRadius: '12px',
            margin: '4px 0',
            height: 'auto',
            lineHeight: '1.4'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
            <span style={{ color: item.color, fontSize: '18px', marginRight: '12px' }}>
              {item.icon}
            </span>
            <span style={{ fontWeight: '500' }}>{item.label}</span>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      padding: '120px 24px 80px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Spin spinning={loading}>
          <Row gutter={[32, 24]}>
            {/* Desktop Sidebar */}
            <Col xs={0} lg={6} xl={6}>
              <Card style={{
                borderRadius: '20px',
                border: 'none',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                position: 'sticky',
                top: '140px'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px',
                  padding: '20px 0'
                }}>
                  <Title level={4} style={{ 
                    margin: 0,
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Profile Sections
                  </Title>
                </div>
                {renderMenuContent()}
              </Card>
            </Col>

            {/* Mobile Menu Button */}
            <Col xs={24} lg={0}>
              <Card style={{
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                marginBottom: '24px'
              }}>
                <Flex justify="space-between" align="center">
                  <Title level={5} style={{ margin: 0 }}>
                    {getCurrentMenuConfig()?.label}
                  </Title>
                  <Button 
                    type="primary" 
                    icon={<OrderedListOutlined />}
                    onClick={showDrawer}
                    style={{
                      borderRadius: '10px',
                      height: '40px'
                    }}
                  >
                    Menu
                  </Button>
                </Flex>
              </Card>
              
              <Drawer 
                placement="left" 
                onClose={onClose} 
                open={visible}
                title="Profile Sections"
                width={300}
                styles={{
                  header: {
                    borderBottom: '1px solid #f0f0f0',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white'
                  },
                  body: { padding: '16px' }
                }}
              >
                {renderMenuContent()}
              </Drawer>
            </Col>

            {/* Main Content */}
            <Col xs={24} lg={18} xl={18}>
              <Card style={{
                borderRadius: '20px',
                border: 'none',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                {/* Header */}
                <div style={{
                  background: `linear-gradient(135deg, ${getCurrentMenuConfig()?.color}, ${getCurrentMenuConfig()?.color}CC)`,
                  padding: '30px',
                  margin: '-24px -24px 32px',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '32px', color: 'white', marginBottom: '12px', display: 'block' }}>
                    {getCurrentMenuConfig()?.icon}
                  </span>
                  <Title level={3} style={{ color: 'white', margin: 0 }}>
                    {getCurrentMenuConfig()?.label}
                  </Title>
                </div>

                {/* Progress Bar */}
                {progress > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <Progress
                      percent={progress}
                      status="active"
                      strokeColor={{
                        '0%': getCurrentMenuConfig()?.color,
                        '100%': `${getCurrentMenuConfig()?.color}CC`,
                      }}
                    />
                  </div>
                )}

                {/* Basic Information Form */}
                {selectedMenuItem === "basic" && (
                  <Form
                    layout="vertical"
                    name="basic"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="profileId" hidden>
                          <Input hidden name="profileId" />
                        </Form.Item>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>First Name</span>}
                          name="firstName"
                          rules={[
                            {
                              required: true,
                              message: "Please input First Name",
                            },
                            {
                              pattern: new RegExp(/^[a-zA-Z ]+$/i),
                              message: "You can only use characters"
                            }
                          ]}
                        >
                          <Input 
                            name="firstName" 
                            placeholder="First Name"
                            size="large"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Last Name</span>}
                          name="lastName"
                          rules={[
                            {
                              required: true,
                              message: "Please input Last Name",
                            },
                            {
                              pattern: new RegExp(/^[a-zA-Z ]+$/i),
                              message: "You can only use characters"
                            }
                          ]}
                        >
                          <Input 
                            name="lastName" 
                            placeholder="Last Name"
                            size="large"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Gender</span>}
                          name="gender"
                        >
                          <Radio.Group
                            disabled
                            optionType="button"
                            buttonStyle="solid"
                            name="gender"
                            size="large"
                          >
                            <Radio.Button value="Male" style={{ borderRadius: '10px 0 0 10px' }}>
                              <Space>
                                <img src={maleLogo} width="20px" alt="Male" />
                                Male
                              </Space>
                            </Radio.Button>
                            <Radio.Button value="Female" style={{ borderRadius: '0 10px 10px 0' }}>
                              <Space>
                                <img src={femaleLogo} width="20px" alt="Female" />
                                Female
                              </Space>
                            </Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Marital Status</span>}
                          name="maritalStatus"
                          rules={[
                            {
                              required: true,
                              message: "Your marital status",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Marital Status"
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.maritals}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Do you have Kids</span>}
                          name="haveKids"
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                            onChange={(e) => {
                              if (e.target.value === "No") {
                                form.setFieldsValue({
                                  kids: 0,
                                  kidsLivingStatus: undefined
                                });
                              }
                            }}
                          >
                            <Radio.Button value="Yes" style={{ borderRadius: '10px 0 0 10px' }}>
                              <Space>
                                <img src={kidsLogo} width="20px" alt="Kids" />
                                Yes
                              </Space>
                            </Radio.Button>
                            <Radio.Button value="No" style={{ borderRadius: '0 10px 10px 0' }}>
                              <Space>
                                <img src={noKidsLogo} width="20px" alt="No Kids" />
                                No
                              </Space>
                            </Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>

                      <Col xs={12} sm={8}>
                        <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.haveKids !== currentValues.haveKids}>
                          {({ getFieldValue }) => {
                            const haveKids = getFieldValue('haveKids');
                            return (
                              <Form.Item
                                label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Kids</span>}
                                name="kids"
                                rules={haveKids === "Yes" ? [
                                  {
                                    required: true,
                                    message: 'Please specify number of kids'
                                  },
                                  {
                                    type: 'number',
                                    min: 1,
                                    message: 'Must be at least 1'
                                  }
                                ] : []}
                              >
                                <InputNumber
                                  min={0}
                                  max={10}
                                  size="large"
                                  style={{ width: '100%', borderRadius: '10px' }}
                                  disabled={haveKids !== "Yes"}
                                  placeholder={haveKids === "Yes" ? "Number of kids" : "0"}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>

                      <Col xs={12} sm={8}>
                        <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.haveKids !== currentValues.haveKids}>
                          {({ getFieldValue }) => {
                            const haveKids = getFieldValue('haveKids');
                            return (
                              <Form.Item
                                label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Kids Living</span>}
                                name="kidsLivingStatus"
                                rules={haveKids === "Yes" ? [
                                  {
                                    required: true,
                                    message: 'Please select living status'
                                  }
                                ] : []}
                              >
                                <Select
                                  showSearch
                                  size="large"
                                  style={{ borderRadius: '10px' }}
                                  options={jsonData.kidsLivingStatus}
                                  allowClear
                                  disabled={haveKids !== "Yes"}
                                  placeholder={haveKids === "Yes" ? "Select living status" : "Not applicable"}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Current Residence</span>}
                          name="currentResidence"
                        >
                          <Input
                            name="currentResidence"
                            placeholder="Residing city"
                            size="large"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Citizen of</span>}
                          name="citizenOf"
                        >
                          <Select
                            placeholder="Citizen Of"
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.currencies}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Mother Tongue</span>}
                          name="motherTongue"
                          rules={[
                            {
                              required: true,
                              message: "Your mother tongue",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Mother Tongue"
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.languages}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Languages you speak</span>}
                          name="spokenLanguages"
                        >
                          <Select
                            mode="multiple"
                            placeholder="Spoken Languages"
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.languages}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update Basic Information
                      </Button>
                    </div>
                  </Form>
                )}

                {/* Religious Information Form */}
                {selectedMenuItem === "religious" && (
                  <Form
                    layout="vertical"
                    name="religious"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Alert
                      message="Religious Information"
                      description="Some fields are disabled as they cannot be changed after profile creation for verification purposes."
                      type="info"
                      showIcon
                      style={{ marginBottom: '24px', borderRadius: '12px' }}
                    />

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Sub Caste</span>}
                          name="subCaste"
                        >
                          <Select
                            disabled
                            showSearch
                            placeholder="Sub Caste"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.subcaste}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Gothram</span>}
                          name="gothram"
                        >
                          <Select
                            disabled
                            showSearch
                            placeholder="Gothram"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.gothram}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={12} sm={8}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Nakshatram</span>}
                          name="nakshatram"
                        >
                          <Select
                            disabled
                            showSearch
                            placeholder="Nakshatram"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.nakshatram}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={4}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Padam</span>}
                          name="padam"
                        >
                          <InputNumber
                            disabled
                            name="padam"
                            value={0}
                            min={0}
                            max={4}
                            size="large"
                            style={{ width: '100%', borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Raasi</span>}
                          name="raasi"
                        >
                          <Select
                            showSearch
                            placeholder="Zodiac Sign/Raasi"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.raasis}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Place of Birth</span>}
                          name="placeOfBirth"
                          rules={[
                            {
                              pattern: new RegExp(/^[a-zA-Z ]+$/i),
                              message: "You can only use characters"
                            }
                          ]}
                        >
                          <Input
                            disabled
                            name="placeOfBirth"
                            placeholder="Place of Birth"
                            size="large"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Time of Birth</span>}
                          name="timeOfBirth"
                        >
                          <Input 
                            disabled 
                            name="timeOfBirth"
                            size="large"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Dosham</span>}
                          name="dosham"
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                            name="dosham"
                            disabled
                          >
                            <Radio.Button value="Yes" style={{ borderRadius: '10px 0 0 10px' }}>Yes</Radio.Button>
                            <Radio.Button value="No" style={{ borderRadius: '0 10px 10px 0' }}>No</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Dosham Type</span>}
                          name="doshamType"
                        >
                          <Select
                            disabled
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.doshamType}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update Religious Information
                      </Button>
                    </div>
                  </Form>
                )}

                {/* Education Information Form */}
                {selectedMenuItem === "education" && (
                  <Form
                    layout="vertical"
                    name="education"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Education</span>}
                          name="education"
                          rules={[
                            {
                              required: true,
                              message: "Please input education",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.education}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Education Details</span>}
                          name="educationDetails"
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.educationDetails}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Additional Education Information</span>}
                          name="additionalEducationInfo"
                        >
                          <TextArea
                            autoSize={{ minRows: 3 }}
                            placeholder="Additional Educational information you want to share"
                            name="additionalEducationInfo"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Employment</span>}
                          name="employment"
                          rules={[
                            {
                              required: true,
                              message: "Please input employment",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.employment}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Occupation</span>}
                          name="occupation"
                          rules={[
                            {
                              required: true,
                              message: "Please input occupation",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.occupation}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Additional Employment Information</span>}
                          name="additionalEmploymentInfo"
                          rules={[
                            {
                              pattern: new RegExp(/^[a-zA-Z0-9'.,; ]+$/i),
                              message: "You can only use characters and basic punctuation"
                            }
                          ]}
                        >
                          <TextArea
                            placeholder="Additional Employment Details you want to share"
                            autoSize={{ minRows: 3 }}
                            name="additionalEmploymentInfo"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Currency</span>}
                          name="currency"
                          rules={[
                            {
                              required: true,
                              message: "Pick a currency",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.currencies}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Salary</span>}
                          name="salary"
                        >
                          <InputNumber
                            style={{ width: '100%', borderRadius: '10px' }}
                            size="large"
                            min={0}
                            max={9999999}
                            placeholder="Enter amount"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Frequency</span>}
                          name="salaryType"
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.salaryType}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update Education Information
                      </Button>
                    </div>
                  </Form>
                )}

                {/* Family Information Form */}
                {selectedMenuItem === "family" && (
                  <Form
                    layout="vertical"
                    name="family"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Father's Occupation</span>}
                          name="fathersOccupation"
                          rules={[
                            {
                              required: true,
                              message: "Father's Occupation?",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.fathersOccupation}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Father's Name</span>}
                          name="fathersName"
                        >
                          <Input 
                            name="fathersName"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            placeholder="Enter father's name"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Mother's Occupation</span>}
                          name="mothersOccupation"
                          rules={[
                            {
                              required: true,
                              message: "Mother's Occupation?",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.mothersOccupation}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Mother's Name</span>}
                          name="mothersName"
                        >
                          <Input 
                            name="mothersName"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            placeholder="Enter mother's name"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={12} sm={6}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Brothers</span>}
                          name="brothers"
                        >
                          <InputNumber
                            name="brothers"
                            style={{ width: '100%', borderRadius: '10px' }}
                            size="large"
                            min={0}
                            max={9}
                            onChange={() => {
                              const brothersMarital = form.getFieldValue('brothersMarital');
                              const brothers = form.getFieldValue('brothers') || 0;
                              if (brothersMarital > brothers) {
                                form.setFieldValue('brothersMarital', brothers);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={12} sm={6}>
                        <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.brothers !== currentValues.brothers}>
                          {({ getFieldValue }) => {
                            const brothersCount = getFieldValue('brothers') || 0;
                            return (
                              <Form.Item
                                label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Brothers Married</span>}
                                name="brothersMarital"
                                rules={[
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      const brothers = getFieldValue('brothers') || 0;
                                      if (value > brothers) {
                                        return Promise.reject(new Error(`Cannot exceed ${brothers} brothers`));
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <InputNumber
                                  name="brothersMarital"
                                  style={{ width: '100%', borderRadius: '10px' }}
                                  size="large"
                                  min={0}
                                  max={brothersCount}
                                  placeholder={`Max: ${brothersCount}`}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>

                      <Col xs={12} sm={6}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Sisters</span>}
                          name="sisters"
                        >
                          <InputNumber
                            name="sisters"
                            style={{ width: '100%', borderRadius: '10px' }}
                            size="large"
                            min={0}
                            max={9}
                            onChange={() => {
                              const sistersMarital = form.getFieldValue('sistersMarital');
                              const sisters = form.getFieldValue('sisters') || 0;
                              if (sistersMarital > sisters) {
                                form.setFieldValue('sistersMarital', sisters);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={12} sm={6}>
                        <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.sisters !== currentValues.sisters}>
                          {({ getFieldValue }) => {
                            const sistersCount = getFieldValue('sisters') || 0;
                            return (
                              <Form.Item
                                label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Sisters Married</span>}
                                name="sistersMarital"
                                rules={[
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      const sisters = getFieldValue('sisters') || 0;
                                      if (value > sisters) {
                                        return Promise.reject(new Error(`Cannot exceed ${sisters} sisters`));
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <InputNumber
                                  name="sistersMarital"
                                  style={{ width: '100%', borderRadius: '10px' }}
                                  size="large"
                                  min={0}
                                  max={sistersCount}
                                  placeholder={`Max: ${sistersCount}`}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Family Values</span>}
                          name="familyValues"
                          rules={[
                            {
                              required: true,
                              message: "Please input Family Values",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Family Values"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.familyValues}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Family Status</span>}
                          name="familyStatus"
                          rules={[
                            {
                              required: true,
                              message: "Please input Family Status",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Family Status"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.familyStatus}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Family Type</span>}
                          name="familyType"
                          rules={[
                            {
                              required: true,
                              message: "Please pick Family Type",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Family Type"
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.familyType}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Native Place</span>}
                          name="nativePlace"
                        >
                          <Input 
                            name="nativePlace" 
                            placeholder="Ancestor Place"
                            size="large"
                            style={{ borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update Family Information
                      </Button>
                    </div>
                  </Form>
                )}

                {/* General Information Form */}
                {selectedMenuItem === "general" && (
                  <Form
                    layout="vertical"
                    name="general"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Smoking</span>}
                          name="smoking"
                          rules={[
                            {
                              required: true,
                              message: "Smoking Habits",
                            },
                          ]}
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                            name="smoking"
                          >
                            <Radio.Button value="Yes" style={{ borderRadius: '10px 0 0 10px' }}>Yes</Radio.Button>
                            <Radio.Button value="Occasional">Occasional</Radio.Button>
                            <Radio.Button value="No" style={{ borderRadius: '0 10px 10px 0' }}>No</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Tobacco</span>}
                          name="tobacco"
                          rules={[
                            {
                              required: true,
                              message: "Tobacco Habits",
                            },
                          ]}
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                            name="tobacco"
                          >
                            <Radio.Button value="Yes" style={{ borderRadius: '10px 0 0 10px' }}>Yes</Radio.Button>
                            <Radio.Button value="Occasional">Occasional</Radio.Button>
                            <Radio.Button value="No" style={{ borderRadius: '0 10px 10px 0' }}>No</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Drinking</span>}
                          name="drinking"
                          rules={[
                            {
                              required: true,
                              message: "Drinking Habits",
                            },
                          ]}
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                            name="drinking"
                          >
                            <Radio.Button value="Yes" style={{ borderRadius: '10px 0 0 10px' }}>Yes</Radio.Button>
                            <Radio.Button value="Socially">Socially</Radio.Button>
                            <Radio.Button value="No" style={{ borderRadius: '0 10px 10px 0' }}>No</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Drugs</span>}
                          name="drugs"
                          rules={[
                            {
                              required: true,
                              message: "Drugs Habits",
                            },
                          ]}
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                            name="drugs"
                          >
                            <Radio.Button value="Yes" style={{ borderRadius: '10px 0 0 10px' }}>Yes</Radio.Button>
                            <Radio.Button value="Sometimes">Sometimes</Radio.Button>
                            <Radio.Button value="No" style={{ borderRadius: '0 10px 10px 0' }}>No</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Dietary Habits</span>}
                          name="diet"
                          rules={[
                            {
                              required: true,
                              message: "Dietary Habits",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.diet}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Height</span>}
                          name="height"
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.height}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={12} sm={8}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Weight</span>}
                          name="weight"
                        >
                          <InputNumber
                            name="weight"
                            value={0}
                            min={30}
                            max={400}
                            size="large"
                            style={{ width: '100%', borderRadius: '10px' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={4}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Unit</span>}
                          name="weightType"
                        >
                          <Select
                            name="weightType"
                            size="large"
                            style={{ width: '100%', borderRadius: '10px' }}
                            options={[
                              { value: 'Lbs', label: 'Lbs' },
                              { value: 'Kg', label: 'Kg' }
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Body Type</span>}
                          name="bodyType"
                          rules={[
                            {
                              required: true,
                              message: "Body Type",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            size="large"
                            style={{ borderRadius: '10px' }}
                            options={jsonData.bodyType}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col xs={24}>
                        <Form.Item 
                          label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Disability</span>}
                          name="disability"
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                            name="disability"
                          >
                            <Radio.Button value="Normal" style={{ borderRadius: '10px 0 0 10px' }}>Normal</Radio.Button>
                            <Radio.Button value="Physically Challenged" style={{ borderRadius: '0 10px 10px 0' }}>
                              Physically Challenged
                            </Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update General Information
                      </Button>
                    </div>
                  </Form>
                )}

                {/* Contact Information Form */}
                {selectedMenuItem === "contact" && (
                  <Form
                    layout="vertical"
                    name="contact"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Form.List name="contact">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <Card
                              key={key}
                              style={{
                                marginBottom: '16px',
                                borderRadius: '16px',
                                border: '2px solid #f0f0f0'
                              }}
                              size="small"
                            >
                              <Row gutter={[16, 16]} align="middle">
                                <Col xs={24} sm={8}>
                                  <Form.Item
                                    {...restField}
                                    label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Contact Name</span>}
                                    name={[name, "contactName"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Missing contact name",
                                      }
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Input 
                                      placeholder="Contact Name"
                                      size="large"
                                      style={{ borderRadius: '10px' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Form.Item
                                    {...restField}
                                    label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Contact Number</span>}
                                    name={[name, "contactNumber"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Missing contact number",
                                      },
                                      {
                                        pattern: new RegExp(/^[0-9()+\s-]+$/i),
                                        message: "You can only use numbers, spaces, brackets and + -"
                                      }
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Input 
                                      placeholder="Contact Number"
                                      size="large"
                                      style={{ borderRadius: '10px' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={12} sm={4}>
                                  <Form.Item
                                    {...restField}
                                    label={<span style={{ fontWeight: '600', fontSize: '15px' }}>Private</span>}
                                    name={[name, "private"]}
                                    valuePropName="checked"
                                    initialValue={true}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Switch
                                      name={[name, "private"]}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={12} sm={4} style={{ textAlign: 'center' }}>
                                  {name >= 0 && (
                                    <Tooltip title="Remove Contact">
                                      <Button
                                        danger
                                        icon={<DeleteFilled />}
                                        onClick={() => remove(name)}
                                        style={{
                                          borderRadius: '50%',
                                          width: '40px',
                                          height: '40px'
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Col>
                              </Row>
                            </Card>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              block
                              icon={<PlusOutlined />}
                              size="large"
                              style={{
                                borderRadius: '12px',
                                height: '60px',
                                fontSize: '16px',
                                borderStyle: 'dashed',
                                borderWidth: '2px'
                              }}
                            >
                              Add Contact Details
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update Contact Information
                      </Button>
                    </div>
                  </Form>
                )}

                {/* UPDATED Photos Information Form with compression */}
                {selectedMenuItem === "photos" && (
                  <Form
                    layout="vertical"
                    name="photos"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Alert
                      message="Smart Photo Upload with Auto-Compression"
                      description={
                        <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                          <li>Maximum 6 photos allowed</li>
                          <li>Only JPG/PNG/WEBP formats accepted</li>
                          <li>Maximum file size: 10MB per photo (before compression)</li>
                          <li>Photos are automatically compressed ~80% while maintaining quality</li>
                          <li>Face detection ensures photo quality</li>
                          <li>Massive Firebase storage cost savings!</li>
                        </ul>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: '24px', borderRadius: '12px' }}
                    />

                    <Form.Item
                      name="profileImages"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                      rules={[
                        {
                          required: true,
                          message: "Please upload at least one photo",
                        },
                        {
                          validator: (_, fileList) => {
                            if (fileList && fileList.length > 6) {
                              return Promise.reject(new Error('Maximum 6 photos allowed'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Upload
                        beforeUpload={beforeUpload}
                        listType="picture-card"
                        accept="image/*"
                        multiple
                        maxCount={6}
                        className="upload-list-inline"
                        style={{ borderRadius: '12px' }}
                      >
                        {form.getFieldValue('profileImages')?.length >= 6 ? null : (
                          <button
                            style={{
                              border: 0,
                              background: "none",
                              borderRadius: '12px'
                            }}
                            type="button"
                          >
                            <PlusOutlined style={{ fontSize: '24px', color: getCurrentMenuConfig()?.color }} />
                            <div style={{
                              marginTop: 8,
                              color: getCurrentMenuConfig()?.color,
                              fontWeight: '500'
                            }}>
                              Upload & Compress
                            </div>
                          </button>
                        )}
                      </Upload>
                    </Form.Item>

                    <Alert
                      message="Auto-Compression Active"
                      description="Your photos will be automatically compressed by ~80% while maintaining visual quality. This saves significant Firebase storage costs without any quality loss visible to viewers."
                      type="success"
                      showIcon
                      style={{ marginBottom: '24px', borderRadius: '12px' }}
                    />

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update Compressed Photos
                      </Button>
                    </div>
                  </Form>
                )}

                {/* About Self Form */}
                {selectedMenuItem === "aboutSelf" && (
                  <Form
                    layout="vertical"
                    name="aboutSelf"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Form.Item
                      label={<span style={{ fontWeight: '600', fontSize: '18px' }}>About Yourself</span>}
                      name="aboutSelf"
                      rules={[
                        {
                          required: true,
                          min: 20,
                          message: "Write a little about yourself (minimum 20 characters)",
                        },
                        {
                          pattern: new RegExp(/^[a-zA-Z0-9'.,;:\s!?-]+$/i),
                          message: "You can only use letters, numbers and basic punctuation"
                        }
                      ]}
                    >
                      <TextArea 
                        name="aboutSelf" 
                        rows={6}
                        placeholder="Tell us about yourself, your interests, values, and what makes you unique..."
                        style={{ borderRadius: '12px', fontSize: '16px' }}
                        showCount
                        maxLength={1000}
                      />
                    </Form.Item>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update About Self
                      </Button>
                    </div>
                  </Form>
                )}

                {/* About Partner Form */}
                {selectedMenuItem === "aboutPartner" && (
                  <Form
                    layout="vertical"
                    name="aboutPartner"
                    style={{ maxWidth: '100%' }}
                    form={form}
                    fields={initialValues}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                  >
                    <Form.Item
                      label={<span style={{ fontWeight: '600', fontSize: '18px' }}>Partner Preferences</span>}
                      name="partnerPreferences"
                      rules={[
                        {
                          required: true,
                          min: 20,
                          message: "Write a little about your partner preferences (minimum 20 characters)",
                        },
                      ]}
                    >
                      <TextArea 
                        name="partnerPreferences" 
                        rows={6}
                        placeholder="Describe your ideal partner, their qualities, background, and what you're looking for in a relationship..."
                        style={{ borderRadius: '12px', fontSize: '16px' }}
                        showCount
                        maxLength={1000}
                      />
                    </Form.Item>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        size="large"
                        style={{
                          height: '48px',
                          padding: '0 48px',
                          borderRadius: '24px',
                          background: getCurrentMenuConfig()?.color,
                          border: 'none',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: `0 8px 20px ${getCurrentMenuConfig()?.color}40`
                        }}
                      >
                        Update Partner Preferences
                      </Button>
                    </div>
                  </Form>
                )}
              </Card>
            </Col>
          </Row>
        </Spin>
      </div>
    </div>
  );
}

export default EditProfile;